import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import * as dealApis from "../../utilities/apis/deal"
import * as productApis from "../../utilities/apis/product"
import Layout from "../../components/Layout"

const Deal = () => {
  const navigate = useNavigate()
  const { token } = useSelector(state => state.user)
  const params = new URLSearchParams(window.location.search)
  const thumbnailRef = useRef(null)
  const thumbnailFileRef = useRef(null)
  const thumbnail64Ref = useRef(null)
  const titleRef = useRef(null)
  const slugRef = useRef(null)
  const descriptionRef = useRef(null)
  const costRef = useRef(null)
  const priceRef = useRef(null)
  const discountRef = useRef(null)
  const saleRef = useRef(null)
  const videoRef = useRef(null)
  const whatsappRef = useRef(null)
  const [discount, setDiscount] = useState(0)
  const [products, setProducts] = useState([])
  const [items, setItems] = useState([])
  const [homePage, setHomePage] = useState(false)

  const options = products.map((product) => (
    <option key={product.id} value={product.id}>{product.title}</option>
  ))

  const setImage = (type, base64) => {
    switch (type) {
      case "thumbnail":
        thumbnailRef.current.src = base64
        thumbnail64Ref.current.value = base64
        break
    }
  }

  const handleChange = async (e) => {
    try {
      if (e.target.files && e.target.files[0]) {
        if (e.target.files[0].size > 1024000) throw new Error("Image size must be maximum of 1MB")
        if (!["image/jpeg", "image/jpg", "image/png"].includes(e.target.files[0].type)) throw new Error("image must be in format of .jpg, .jpeg or .png")

        const reader = new FileReader()
        const type = e.target.id

        reader.addEventListener("load", function (e) {
          setImage(type, e.target.result)
        })

        reader.readAsDataURL(e.target.files[0])
      }
    } catch (error) {
      alert(error.message)
    }
  }

  const handleClear = (type) => {
    switch (type) {
      case "thumbnail":
        thumbnailRef.current.src = "/upload.png"
        thumbnailFileRef.current.value = ""
        thumbnail64Ref.current.value = ""
        break
    }
  }

  const handleSubmit = async (e) => {
    try {
      e.preventDefault()
      if (items.length < 2) throw new Error("To create a deal please add 2 or more products")

      const formData = new FormData()

      if (e.target.thumbnail.files[0]) formData.append("deal[thumbnail_url]", e.target.thumbnail.files[0])
      else formData.append("deal[thumbnail_url]", thumbnail64Ref.current.value)

      formData.append("deal[title]", titleRef.current ? titleRef.current.value : '')
      formData.append("deal[slug]", slugRef.current ? slugRef.current.value : '')
      formData.append("deal[description]", descriptionRef.current ? descriptionRef.current.value : '')
      formData.append("deal[cost]", costRef.current ? costRef.current.value : '')
      formData.append("deal[price]", priceRef.current ? priceRef.current.value : '')
      formData.append("deal[discount]", discountRef.current ? discountRef.current.value : '')
      formData.append("deal[whatsapp_url]", whatsappRef.current ? whatsappRef.current.value : '')
      formData.append("deal[video_url]", videoRef.current ? videoRef.current.value : '')
      formData.append("deal[home_page]", homePage)
      items.forEach((item, index) => {
        formData.append(`deal_products[${index}][quantity]`, item.quantity)
        formData.append(`deal_products[${index}][product_id]`, item.product_id)
      })

      if (!params.has("id")) {
        const response = await dealApis.createDeal(token, formData)
        if (!response.status) throw new Error(response.message)

        titleRef.current.value = ""
        slugRef.current.value = ""
        descriptionRef.current.value = ""
        costRef.current.value = ""
        priceRef.current.value = ""
        discountRef.current.value = ""
        saleRef.current.value = ""
        whatsappRef.current.value = ""
        videoRef.current.value = ""
        handleClear("thumbnail")
        setItems([])
        setDiscount(0)
      } else {
        formData.append("deal[id]", parseInt(params.get("id")))
        const response = await dealApis.updateDeal(token, formData)
        if (!response.status) throw new Error(response.message)
      }

      alert("Deal saved")
    } catch (error) {
      alert(error.message)
    }
  }

  const handleItem = () => {
    const product = products[0]
    const item = {
      quantity: 1,
      product_id: product.id,
      product: {
        cost: product.cost,
        price: product.price
      }
    }
    setItems(previous => [...previous, item])
  }

  const handleItemDelete = (index) => {
    setItems(previous => {
      previous.splice(index, 1)
      return [...previous]
    })
  }

  const handleItemChange = (e, index, type) => {
    switch (type) {
      case "id":
        setItems(previous => {
          const product = products.find(product => product.id === parseInt(e.target.value))
          const item = {
            quantity: previous[index].quantity,
            product_id: product.id,
            product: {
              cost: product.cost,
              price: product.price
            }
          }
          previous[index] = item
          return [...previous]
        })
        break
      case "quantity":
        setItems(previous => {
          previous[index].quantity = parseInt(e.target.value)
          return [...previous]
        })
        break
    }
  }

  const getProducts = async () => {
    try {
      const response = await productApis.getProducts(token)
      if (!response.status) throw new Error(response.message)

      setProducts(response.products)
    } catch (error) {
      alert(error.message)
    }
  }

  const getDeal = async () => {
    try {
      const response = await dealApis.getDeal(token, params.get("id"))
      if (!response.status) throw new Error(response.message)

      setHomePage(response.deal.home_page)
      titleRef.current.value = response.deal.title
      slugRef.current.value = response.deal.slug
      descriptionRef.current.value = response.deal.description
      costRef.current.value = response.deal.cost
      priceRef.current.value = response.deal.price
      discountRef.current.value = response.deal.discount
      saleRef.current.value = response.deal.price - response.deal.discount
      videoRef.current.value = response.deal.video_url
      whatsappRef.current.value = response.deal.whatsapp_url

      thumbnailRef.current.src = response.deal.thumbnail_url
      thumbnail64Ref.current.value = response.deal.thumbnail_url

      setItems(response.deal.deal_products)
      setDiscount(response.deal.discount)
    } catch (error) {
      alert(error.message)
    }
  }

  useEffect(() => {
    getProducts()
    if (params.has("id")) getDeal()
  }, [])

  const cost = items.reduce((p, c) => p += c.product.cost * c.quantity, 0)
  const price = items.reduce((p, c) => p += c.product.price * c.quantity, 0)

  if (costRef && costRef.current)
    costRef.current.value = cost
  if (priceRef && priceRef.current) {
    priceRef.current.value = price
    saleRef.current.value = price - (discount || 0)
  }

  return (
    <Layout
      header={true}
      footer={true}
    >
      <main className="py-3">
        <div className="container-fluid">
          <form method="POST" onSubmit={handleSubmit}>
            <div className="d-flex flex-column gap-3">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex gap-3 align-items-center">
                  <button type="button" className="btn btn-secondary rounded-4" onClick={() => navigate(-1)}><i className="bi bi-chevron-left"></i></button>
                  <p className="lead text-body-emphasis fw-bold m-0">{params.has("id") ? "Edit" : "Add"} Deal</p>
                </div>
                <button type="submit" className="btn btn-theme rounded-4">Save</button>
              </div>

              <div className="row g-3">
                <div className="col-md-8">

                  <div className="card rounded-4 shadow-none mb-3">
                    <div className="card-body py-4">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <p className="lead text-body-emphasis fw-bold m-0">Deal Details</p>
                        <div className="form-check">
                          <input className="form-check-input" type="checkbox" id="home-page" checked={homePage} onChange={() => setHomePage(!homePage)} />
                          <label className="form-check-label" htmlFor="home-page">Display on Home Page</label>
                        </div>
                      </div>

                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label htmlFor="title" className="form-label">Title</label>
                          <input ref={titleRef} type="text" className="form-control rounded-3" id="title" required />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="slug" className="form-label">Slug</label>
                          <input ref={slugRef} type="text" className="form-control rounded-3" id="slug" required />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="description" className="form-label">Description</label>
                        <textarea ref={descriptionRef} type="text" className="form-control rounded-3" id="description" rows={8} required></textarea>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-4">
                    <div className="bg-secondary border-bottom rounded-top-4 p-3 d-flex justify-content-between align-items-center">
                      <div>
                        <p className="lead text-body-emphasis fw-bold m-0">Deal Items</p>
                        <p className="text-body-tertiary fw-semibold m-0">{items.length} products</p>
                      </div>
                      <button type="button" className="btn btn-light-theme rounded-3" onClick={handleItem}><i className="bi bi-plus-lg"></i> Add Product</button>
                    </div>
                    <table className="table shadow-none m-0">
                      <thead className="table-light">
                        <tr>
                          <th scope="col" style={{ width: "40%" }} className="align-middle">Product</th>
                          <th scope="col" style={{ width: "24%" }} className="align-middle">Quantity</th>
                          <th scope="col" style={{ width: "12%" }} className="align-middle text-center">Cost</th>
                          <th scope="col" style={{ width: "12%" }} className="align-middle text-center">Price</th>
                          <th scope="col" style={{ width: "12%" }} className="align-middle text-center"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.length > 0 && items.map((item, index) => {
                          return (
                            <tr key={index.toString()}>
                              <td className="align-middle"><select className="form-select rounded-3" defaultValue={item.product_id} onChange={(e) => handleItemChange(e, index, "id")}>{options}</select></td>
                              <td className="align-middle"><input type="number" className="form-control rounded-3" defaultValue={item.quantity} onChange={(e) => handleItemChange(e, index, "quantity")} onKeyUp={(e) => handleItemChange(e, index, "quantity")} /></td>
                              <td className="align-middle text-center">Rs. {item.product.cost * item.quantity}</td>
                              <td className="align-middle text-center">Rs. {item.product.price * item.quantity}</td>
                              <td className="align-middle text-center">
                                <button type="button" className="btn btn-sm btn-light-danger rounded-3" onClick={() => handleItemDelete(index)}><i className="bi bi-x-lg"></i></button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td className="fs-6 text-body-tertiary fw-bold align-middle" colSpan={2}>Items Total</td>
                          <td className="fs-6 text-body-tertiary fw-bold align-middle text-center">Rs. {cost}</td>
                          <td className="fs-6 text-body-tertiary fw-bold align-middle text-center">Rs. {price}</td>
                          <td className="fs-6 text-body-tertiary fw-bold align-middle text-center"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                </div>
                <div className="col-md-4">

                  <div className="card rounded-4 shadow-none mb-3">
                    <div className="card-body py-4">
                      <p className="lead text-body-emphasis fw-bold mb-4">Pricing</p>

                      <div className="mb-3">
                        <label htmlFor="cost" className="form-label">Cost</label>
                        <input ref={costRef} type="number" className="form-control rounded-3" id="cost" required disabled />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="price" className="form-label">Price</label>
                        <input ref={priceRef} type="number" className="form-control rounded-3" id="price" step="0.01" required />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="discount" className="form-label">Discount</label>
                        <input ref={discountRef} type="number" className="form-control rounded-3" id="discount" step="0.01" required onKeyUp={(e) => setDiscount(parseFloat(e.target.value))} />
                      </div>
                      <div>
                        <label htmlFor="sale" className="form-label">Sale</label>
                        <input ref={saleRef} type="number" className="form-control rounded-3" id="sale" required disabled />
                      </div>
                    </div>
                    <div className="card-footer border-top-0 d-flex justify-content-between">
                      <p className="text-body-tertiary fw-bold m-0">Customer Pays</p>
                      <p className="text-theme fw-bold m-0">Rs. {price - (discount || 0)}</p>
                    </div>
                  </div>

                  <div className="card rounded-4 shadow-none mb-3">
                    <div className="card-body py-4">
                      <p className="lead text-body-emphasis fw-bold mb-4">Media</p>

                      <div className="card rounded-3 shadow-none position-relative mb-3">
                        <div className="card-body p-0">
                          <input ref={thumbnail64Ref} type="hidden" />
                          <input ref={thumbnailFileRef} type="file" id="thumbnail" className="form-control d-none" accept=".jpg, .jpeg, .png" onChange={handleChange} />
                          <img ref={thumbnailRef} src="/upload-white.png" className="rounded" onClick={() => thumbnailFileRef.current.click()} />
                        </div>
                      </div>

                      <button type="button" className="btn btn-light-danger w-100 rounded-3" onClick={() => handleClear("thumbnail")}>Clear</button>
                    </div>
                  </div>

                  <div className="card rounded-4 shadow-none">
                    <div className="card-body py-4">
                      <p className="lead text-body-emphasis fw-bold mb-4">Links</p>

                      <div className="mb-3">
                        <label htmlFor="whatsapp_url" className="form-label">Whatsapp Url</label>
                        <input ref={whatsappRef} type="text" className="form-control" id="whatsapp" placeholder="https://wa.me/..." />
                      </div>
                      <div>
                        <label htmlFor="video" className="form-label">Video Url</label>
                        <input ref={videoRef} type="text" className="form-control" id="video" placeholder="https://youtube.com/embed/..." />
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </form>
        </div>
      </main>
    </Layout>
  )
}

export default Deal
